/**
 * AdminUserRolesSection — przypisywanie ról ITE użytkownikowi (RBAC, Krok 1a.4).
 *
 * Osadzane w AdminUserDrawer. Lista wszystkich ról jako checkboxy; zaznaczone =
 * przypisane userowi. Zapis przez PUT /admin/admin-users/:id/roles (replace-all).
 *
 * Backend guard CANNOT_REMOVE_OWN_MANAGE_ROLE: edytując własne konto nie można
 * odebrać sobie ostatniej roli dającej zarządzanie rolami (anty-lockout) — błąd
 * obsłużony inline.
 *
 * To nowy system (multi-role). Stara kolumna admin_users.role (single varchar)
 * edytowana osobno w sekcji „profil" drawera — legacy do czasu usunięcia kolumny.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface RoleRow {
  id: string;
  code: string;
  name: string;
  isSystem: boolean;
}

export interface AdminUserRolesSectionProps {
  userId: string;
}

export function AdminUserRolesSection({ userId }: AdminUserRolesSectionProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const allRolesQuery = useQuery<{ roles: RoleRow[] }>({
    queryKey: ['admin', 'admin-roles'],
    queryFn: async () => (await api.get('/admin/admin-roles')).data,
    retry: false,
  });

  const userRolesQuery = useQuery<{ roles: RoleRow[] }>({
    queryKey: ['admin', 'admin-users', userId, 'roles'],
    queryFn: async () => (await api.get(`/admin/admin-users/${userId}/roles`)).data,
    retry: false,
  });

  const [draft, setDraft] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (userRolesQuery.data) {
      setDraft(new Set(userRolesQuery.data.roles.map((r) => r.id)));
    }
  }, [userRolesQuery.data]);

  const isDirty = useMemo(() => {
    const saved = new Set(userRolesQuery.data?.roles.map((r) => r.id) ?? []);
    if (saved.size !== draft.size) return true;
    for (const id of draft) if (!saved.has(id)) return true;
    return false;
  }, [userRolesQuery.data, draft]);

  const saveMutation = useMutation({
    mutationFn: async () =>
      (await api.put(`/admin/admin-users/${userId}/roles`, { roleIds: [...draft] })).data,
    onSuccess: () => {
      toast.show({ variant: 'success', description: t('adminRoles.userRoles.saved') });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'admin-users', userId, 'roles'],
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'admin-roles'] });
    },
    onError: (err) => {
      const code = (err as AxiosError<{ error?: string }>).response?.data?.error;
      toast.show({
        variant: 'error',
        description:
          code === 'CANNOT_REMOVE_OWN_MANAGE_ROLE'
            ? t('adminRoles.userRoles.cannotRemoveOwnManage')
            : t('adminRoles.userRoles.saveError'),
      });
    },
  });

  function toggle(roleId: string) {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }

  const loading = allRolesQuery.isLoading || userRolesQuery.isLoading;

  return (
    <section className="space-y-3 border-t border-border-secondary pt-6">
      <h3 className="text-sm font-semibold text-text-primary">{t('adminRoles.userRoles.title')}</h3>
      <p className="text-xs text-text-tertiary">{t('adminRoles.userRoles.description')}</p>

      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : allRolesQuery.isError || userRolesQuery.isError ? (
        <p className="text-xs text-red-600 dark:text-red-400">
          {t('adminRoles.userRoles.loadError')}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            {allRolesQuery.data?.roles.map((role) => (
              <Checkbox
                key={role.id}
                checked={draft.has(role.id)}
                onChange={() => toggle(role.id)}
                label={
                  <span className="inline-flex items-center gap-2">
                    {role.name}
                    {role.isSystem && <Badge color="gray">{t('adminRoles.systemBadge')}</Badge>}
                  </span>
                }
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!isDirty || saveMutation.isPending}
              loading={saveMutation.isPending}
            >
              {t('adminRoles.userRoles.save')}
            </Button>
            {isDirty && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                {t('adminRoles.unsavedChanges')}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
